import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, message, Card, Space, Upload, Tag, Tooltip, Modal } from 'antd';
import { DeleteOutlined, PlusOutlined, UploadOutlined, SaveOutlined, SearchOutlined, BookOutlined, ArrowUpOutlined, ArrowDownOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCourses, createRoadmap, updateRoadmap, uploadRoadmapImage, getRoadmap, uploadRoadmapImageGeneral, searchCourses } from './api';
import { Course } from './types';
import config from './config';

const { TextArea } = Input;
const { Option } = Select;

interface RoadmapFormProps {
  roadmap?: {
    id: number;
    title: string;
    description: string;
    goal: string;
    difficulty: string;
    duration: string;
    imageUrl?: string;
    isActive: boolean;
    roadmapCourses: {
      id: number;
      order: number;
      isRequired: boolean;
      course: {
        id: number;
        title: string;
      };
    }[];
  };
  onCancel: () => void;
  onSuccess: () => void;
}

const RoadmapForm: React.FC<RoadmapFormProps> = ({ roadmap, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [selectedCourses, setSelectedCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Course[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();

  const { data: courses, isLoading: coursesLoading } = useQuery({
    queryKey: ['courses'],
    queryFn: getCourses,
  });

  const coursesList = courses as Course[] || [];

  const { data: roadmapData, isLoading: roadmapLoading } = useQuery({
    queryKey: ['roadmap', roadmap?.id],
    queryFn: () => getRoadmap(roadmap!.id),
    enabled: !!roadmap?.id && !roadmap.title,
  });

  const fullRoadmap = roadmap?.title ? roadmap : roadmapData;

  const createMutation = useMutation({
    mutationFn: createRoadmap,
    onSuccess: () => {
      message.success('Roadmap успешно создан');
      queryClient.invalidateQueries({ queryKey: ['roadmaps'] });
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.message || 'Ошибка при создании roadmap');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateRoadmap(id, data),
    onSuccess: () => {
      message.success('Roadmap успешно обновлен');
      queryClient.invalidateQueries({ queryKey: ['roadmaps'] });
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.message || 'Ошибка при обновлении roadmap');
    },
  });

  useEffect(() => {
    if (fullRoadmap) {
      // Убеждаемся, что isActive имеет правильный тип boolean
      const isActiveValue = Boolean(fullRoadmap.isActive);
      
      form.setFieldsValue({
        title: fullRoadmap.title,
        description: fullRoadmap.description,
        goal: fullRoadmap.goal,
        difficulty: fullRoadmap.difficulty,
        duration: fullRoadmap.duration,
        isActive: isActiveValue,
        imageUrl: fullRoadmap.imageUrl || undefined,
      });
      
      setImagePreview(fullRoadmap.imageUrl || undefined);
      
      const roadmapCourses = fullRoadmap.roadmapCourses
        ?.sort((a: any, b: any) => a.order - b.order)
        .map((rc: any) => ({
          id: rc.course.id,
          title: rc.course.title,
        })) || [];
      setSelectedCourses(roadmapCourses);
      setHasChanges(false);
    }
  }, [fullRoadmap, form]);

  const handleSubmit = async (values: any) => {
    const courseIds = selectedCourses.map(course => course.id);
    
    const roadmapData = {
      ...values,
      courseIds,
      imageUrl: values.imageUrl 
        ? values.imageUrl.replace(/^http:\/\//, 'https://')
        : values.imageUrl,
    };

    if (fullRoadmap) {
      updateMutation.mutate({ id: fullRoadmap.id, data: roadmapData });
    } else {
      createMutation.mutate(roadmapData);
    }
    setHasChanges(false);
  };

  // --- Загрузка изображения ---
  const handleImageUpload = async (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
      return;
    }
    if (info.file.status === 'done') {
      setUploading(false);
      const url = info.file.response?.url;
      if (url) {
        // Автоматически заменяем http:// на https:// для URL изображения
        const secureUrl = url.replace(/^http:\/\//, 'https://');
        form.setFieldsValue({ imageUrl: secureUrl });
        setImagePreview(secureUrl);
        setHasChanges(true);
        message.success('Изображение успешно загружено');
      }
    } else if (info.file.status === 'error') {
      setUploading(false);
      message.error('Ошибка загрузки изображения');
    }
  };

  const selectCourseFromSearch = (course: Course) => {
    if (!selectedCourses.find(c => c.id === course.id)) {
      setSelectedCourses([...selectedCourses, course]);
      setSearchQuery('');
      setShowSearchResults(false);
      setHasChanges(true);
      message.success(`Курс "${course.title}" добавлен в roadmap`);
    } else {
      message.warning('Этот курс уже добавлен в roadmap');
    }
  };

  const handleSearchCourses = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const result = await searchCourses(query, 10, 0);
      setSearchResults(result.data);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Ошибка поиска курсов:', error);
      message.error('Ошибка поиска курсов: ' + (error as Error).message);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        handleSearchCourses(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.search-container')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const removeCourse = (courseId: number) => {
    setSelectedCourses(selectedCourses.filter(c => c.id !== courseId));
    setHasChanges(true);
  };

  const moveCourse = (fromIndex: number, toIndex: number) => {
    const newCourses = [...selectedCourses];
    const [movedCourse] = newCourses.splice(fromIndex, 1);
    newCourses.splice(toIndex, 0, movedCourse);
    setSelectedCourses(newCourses);
    setHasChanges(true);
  };

  const availableSearchResults = searchResults.filter(course => 
    !selectedCourses.find(selected => selected.id === course.id)
  );

  const handleCancel = () => {
    if (hasChanges) {
      Modal.confirm({
        title: 'Подтверждение',
        content: 'У вас есть несохраненные изменения. Вы уверены, что хотите выйти?',
        okText: 'Да, выйти',
        cancelText: 'Отмена',
        onOk: () => {
          setHasChanges(false);
          onCancel();
        },
      });
    } else {
      onCancel();
    }
  };

  if (roadmapLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка данных roadmap...</p>
        </div>
      </div>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            style={{ padding: 0, height: 'auto' }}
          />
          {fullRoadmap ? 'Редактировать roadmap' : 'Создать новый roadmap'}
        </div>
      }
      style={{ maxWidth: 800, margin: '0 auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={() => setHasChanges(true)}
      >
        <Form.Item
          name="title"
          label="Название roadmap"
          rules={[{ required: true, message: 'Введите название roadmap' }]}
        >
          <Input placeholder="Например: Middle Python Developer" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Описание"
          rules={[{ required: true, message: 'Введите описание roadmap' }]}
        >
          <TextArea
            rows={4}
            placeholder="Краткое описание roadmap и что пользователь получит после прохождения"
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item
          name="goal"
          label="Цель обучения"
          rules={[{ required: true, message: 'Введите цель обучения' }]}
        >
          <TextArea
            rows={3}
            placeholder="Конкретная цель, которую достигнет пользователь (например: стать Middle Python разработчиком)"
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Form.Item
          name="difficulty"
          label="Сложность"
          rules={[{ required: true, message: 'Выберите сложность' }]}
        >
          <Select placeholder="Выберите уровень сложности">
            <Option value="beginner">Начинающий</Option>
            <Option value="intermediate">Средний</Option>
            <Option value="advanced">Продвинутый</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="duration"
          label="Длительность"
          rules={[{ required: true, message: 'Введите длительность' }]}
        >
          <Input placeholder="Например: 4-6 месяцев" />
        </Form.Item>

        <Form.Item
          name="isActive"
          label="Статус"
          rules={[{ required: true, message: 'Пожалуйста, выберите статус' }]}
        >
          <Select placeholder="Выберите статус">
            <Option value={true}>Активен</Option>
            <Option value={false}>Неактивен</Option>
          </Select>
        </Form.Item>

        {/* Загрузка изображения */}
        <Form.Item label="Обложка roadmap">
          <Upload
            name="file"
            action={`${config.apiUrl}/roadmaps/upload`}
            headers={{ Authorization: `Bearer ${localStorage.getItem('jwt') || ''}` }}
            showUploadList={false}
            onChange={handleImageUpload}
            accept="image/*"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              Загрузить изображение
            </Button>
          </Upload>
          {imagePreview && (
            <div style={{ marginTop: 12 }}>
              <img 
                src={imagePreview} 
                alt="Обложка roadmap" 
                style={{ maxWidth: 200, borderRadius: 8, boxShadow: '0 2px 8px #eee' }} 
              />
            </div>
          )}
        </Form.Item>

        {/* Поле ввода для imageUrl */}
        <Form.Item
          name="imageUrl"
          label="URL изображения"
        >
          <Input 
            placeholder="https://example.com/image.png" 
            onChange={(e) => {
              const value = e.target.value;
              if (value && value.startsWith('http://')) {
                const secureUrl = value.replace(/^http:\/\//, 'https://');
                form.setFieldsValue({ imageUrl: secureUrl });
                setImagePreview(secureUrl);
              } else {
                setImagePreview(value || null);
              }
              setHasChanges(true);
            }}
          />
        </Form.Item>

        {/* Курсы */}
        <div className="space-y-4">
          <div>
            {/* Добавление курса */}
            <div className="mb-4">
              {/* Поиск курсов */}
              <div className="relative mb-6 search-container">
                <div className="relative">
                  <Input
                    placeholder="🔍 Поиск курсов по названию или описанию..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    prefix={
                      isSearching ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                      ) : (
                        <SearchOutlined className="text-gray-400" />
                      )
                    }
                    size="large"
                    style={{ 
                      borderRadius: 12,
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px',
                      fontSize: 16,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                    }}
                  />
                  
                  {/* Индикатор поиска */}
                  {isSearching && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <div className="flex items-center gap-2 text-blue-500 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                        <span>Поиск...</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Результаты поиска */}
                {showSearchResults && availableSearchResults.length > 0 && (
                  <div className="absolute z-20 w-full mt-2">
                    <Card 
                      size="small" 
                      className="shadow-lg border-0"
                      bodyStyle={{ padding: '10px' }}
                    >
                      <div className="max-h-60 overflow-y-auto">
                        {availableSearchResults.map((course, index) => (
                          <div style={{padding: '10px', margin: '10px', borderRadius: '10px', backgroundColor: 'rgb(212 237 245)'}}
                            key={course.id}
                            className=""
                            onClick={() => selectCourseFromSearch(course)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                {index + 1}.
                              </div>
                              <span className="text-gray-700 font-medium">
                                {course.title}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                )}
                

              </div>

            </div>

            {/* Список выбранных курсов */}
            <div className="space-y-3">
              {selectedCourses.length > 0 && (
                <div style={{margin: '5px'}}  className="mb-4">
                  <div className="flex items-center gap-2 mb-3">
                  <BookOutlined className="text-purple-500" />
                  <span className="font-semibold text-gray-700">Выбранные курсы ({selectedCourses.length})</span>
                </div>
                </div>
              )}
              
              {selectedCourses.map((course, index) => (
                <Card
                  style={{margin: '5px', backgroundColor: 'rgb(247 250 249)'}}
                  key={course.id}
                  size="small"
                  className="mb-3 hover:shadow-md transition-all duration-200"
                  bodyStyle={{ padding: '16px' }}
                >
                  <div className="flex items-center justify-between">
                    {/* Левая часть - номер и название */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}.
                      </div>
                      <div>
                        <div className="text-gray-900 font-semibold">
                          <b>{course.title}</b>
                        </div>
                        {course.description && (
                          <div className="text-sm text-gray-500 mt-1">
                            {course.description}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Правая часть - кнопки управления */}
                    <div className="flex items-center gap-2">
                      {index > 0 && (
                        <Tooltip title="Переместить вверх">
                          <Button
                            size="small"
                            type="primary"
                            icon={<ArrowUpOutlined />}
                            onClick={() => moveCourse(index, index - 1)}
                            className="flex items-center justify-center"
                            style={{ width: 32, height: 32, margin: '3px' }}
                          />
                        </Tooltip>
                      )}
                      {index < selectedCourses.length - 1 && (
                        <Tooltip title="Переместить вниз">
                          <Button
                            size="small"
                            type="primary"
                            icon={<ArrowDownOutlined />}
                            onClick={() => moveCourse(index, index + 1)}
                            className="flex items-center justify-center"
                            style={{ width: 32, height: 32, margin: '3px' }}
                          />
                        </Tooltip>
                      )}
                      <Tooltip title="Удалить курс">
                        <Button
                          size="small"
                          type="primary"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeCourse(course.id)}
                          className="flex items-center justify-center"
                          style={{ width: 32, height: 32, margin: '3px' }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                </Card>
              ))}
              
              {/* Сообщение, если курсы не выбраны */}
               {selectedCourses.length === 0 && (
                 <div className="relative overflow-hidden">
                   <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 opacity-50 rounded-2xl"></div>
                   
                   <div className="relative text-center py-12 px-8 bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm">
                     <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-xs text-gray-600">
                       <SearchOutlined className="text-gray-400" />
                       <span>Используйте поиск выше для добавления курсов</span>
                     </div>
                     
                     <div className="absolute top-4 right-4 w-8 h-8 bg-purple-100 rounded-full opacity-50"></div>
                     <div className="absolute bottom-4 left-4 w-6 h-6 bg-blue-100 rounded-full opacity-50"></div>
                   </div>
                 </div>
               )}
            </div>
          </div>
        </div>

        {/* Кнопки действий */}
        <Form.Item>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>
              Отмена
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={createMutation.isPending || updateMutation.isPending}
              icon={fullRoadmap ? <SaveOutlined /> : <PlusOutlined />}
            >
              {fullRoadmap ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default RoadmapForm;
